import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import { ArrowRight, CheckCircle2, MapPin, Clock, ShieldCheck } from "lucide-react";

function ServiceSchema({ page }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": page.serviceType,
    "name": page.h1,
    "url": `https://sitecleanup.com.au${page.path}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Site Cleanup Melbourne",
      "url": "https://sitecleanup.com.au",
      "email": "sitecleanup@imperiumtech.com.au",
    },
    "areaServed": [
      { "@type": "Place", "name": "Melbourne, VIC, Australia" },
      "Epping", "Craigieburn", "Wollert", "Donnybrook", "Mernda", "Tarneit", "Clyde", "Werribee",
    ],
    "description": page.metaDesc,
    "offers": { "@type": "Offer", "priceCurrency": "AUD", "priceSpecification": { "@type": "PriceSpecification", "priceCurrency": "AUD" } },
  };
  const safe = JSON.stringify(data).replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026");
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safe }} />;
}

function FAQSchema({ faq }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
  const safe = JSON.stringify(data).replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026");
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safe }} />;
}

export default function ServiceLanding({ page }) {
  return (
    <PublicLayout>
      <Seo title={page.metaTitle} description={page.metaDesc} path={page.path} />
      <ServiceSchema page={page} />
      <FAQSchema faq={page.faq} />

      {/* HERO */}
      <section className="bg-asphalt text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 rs-diag opacity-25" />
        <div className="rs-container relative">
          <div className="flex items-center gap-2 text-xs text-white/60 uppercase tracking-widest" data-testid={`svc-breadcrumb-${page.slug}`}>
            <Link to="/" className="hover:text-safety">Home</Link>
            <span>/</span>
            <span className="text-white/85">{page.crumb}</span>
          </div>
          <span className="rs-eyebrow text-safety mt-6 block">{page.eyebrow}</span>
          <h1 className="mt-3 text-5xl md:text-7xl uppercase font-display leading-[0.95] max-w-4xl" data-testid={`svc-h1-${page.slug}`}>
            {page.h1}
          </h1>
          <p className="mt-6 max-w-3xl text-white/80 text-base md:text-lg leading-relaxed">
            {page.intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/request-quote" className="rs-btn-primary" data-testid={`svc-cta-hero-${page.slug}`}>
              {page.cta} <ArrowRight size={16} className="ml-2" />
            </Link>
            <Link to="/services" className="rs-btn-outline-light">Browse all services</Link>
          </div>
          <div className="mt-10 flex flex-wrap gap-6 text-white/70 text-sm">
            <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-safety" /> Free & no obligation</span>
            <span className="flex items-center gap-2"><Clock size={16} className="text-safety" /> Reply within 1 business day</span>
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-safety" /> Vetted local operator</span>
          </div>
        </div>
      </section>

      {/* SERVICE CARDS */}
      <section className="py-20 bg-concrete border-y border-border">
        <div className="rs-container">
          <div className="max-w-2xl">
            <span className="rs-eyebrow">What we cover</span>
            <h2 className="mt-3 text-3xl md:text-4xl uppercase leading-tight font-display">{page.cardsTitle}</h2>
            <p className="mt-4 text-muted-foreground">{page.cardsSub}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {page.cards.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="rs-card h-full flex flex-col" data-testid={`svc-card-${page.slug}-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="w-11 h-11 grid place-items-center bg-safety/10 text-safety">
                    <Icon size={20} />
                  </span>
                  <p className="mt-4 font-display uppercase text-lg leading-tight">{c.title}</p>
                  <p className="mt-2 text-sm text-muted-foreground flex-1">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-white">
        <div className="rs-container">
          <div className="max-w-2xl">
            <span className="rs-eyebrow">How it works</span>
            <h2 className="mt-3 text-3xl md:text-4xl uppercase leading-tight font-display">Three steps to a quote</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Request a quote", d: "Fill in the form with your address, photos and job details. Takes about 2 minutes." },
              { n: "02", t: "Get matched", d: "We pass your enquiry to one vetted local operator who services your suburb." },
              { n: "03", t: page.step3, d: page.step3d },
            ].map((s) => (
              <div key={s.n} className="border-l-2 border-safety pl-6 py-2" data-testid={`svc-step-${page.slug}-${s.n}`}>
                <p className="text-safety font-display text-3xl">{s.n}</p>
                <p className="mt-2 font-display uppercase text-base tracking-wide">{s.t}</p>
                <p className="mt-2 text-sm text-asphalt leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-concrete border-y border-border">
        <div className="rs-container grid md:grid-cols-3 gap-10">
          <div>
            <span className="rs-eyebrow">FAQ</span>
            <h2 className="mt-3 text-3xl md:text-4xl uppercase leading-tight font-display">{page.faqTitle}</h2>
            <p className="mt-4 text-muted-foreground">Something else on your mind? Add it to the "message" field on the quote form and we'll answer with your written quote.</p>
          </div>
          <div className="md:col-span-2 space-y-4">
            {page.faq.map((f, i) => (
              <div key={i} className="border-l-2 border-safety pl-5 py-2 bg-white p-5" data-testid={`svc-faq-${page.slug}-${i}`}>
                <p className="font-display uppercase text-base tracking-wide">{f.q}</p>
                <p className="mt-2 text-sm text-asphalt leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-safety text-white">
        <div className="rs-container grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="rs-eyebrow text-white/80">Ready when you are</span>
            <h2 className="mt-3 text-3xl md:text-5xl uppercase font-display leading-tight">{page.finalTitle}</h2>
            <p className="mt-3 text-white/90 max-w-lg">{page.finalSub}</p>
            <div className="mt-6 flex items-center gap-2 text-white/80 text-sm">
              <MapPin size={14} /> Covering Melbourne & the growth corridors
            </div>
          </div>
          <div className="md:justify-self-end">
            <Link
              to="/request-quote"
              className="inline-flex items-center gap-2 bg-asphalt text-white px-8 py-4 font-semibold uppercase tracking-widest border-2 border-asphalt hover:bg-white hover:text-asphalt transition-colors"
              data-testid={`svc-cta-final-${page.slug}`}
            >
              {page.cta} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
